/*
  One person, opened from their tile.

  *** WIDE, NOT TALL ***
  The first version stacked portrait, name, four facts and seven badges in
  one column: a narrow strip taller than the screen. A dialog that scrolls
  is a dialog whose last row nobody reads. The portrait and the figures now
  sit side by side and the badges run across the full width underneath, so
  everything is visible at once.
*/
.backdrop {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-4);
  background: rgb(0 0 0 / 55%);
}

.card {
  position: relative;
  width: min(34rem, 100%);
  /* Scrolls only if it genuinely cannot fit — a long name or a very short
     window — rather than by default. */
  max-height: calc(100vh - var(--space-6));
  overflow-y: auto;
  display: grid;
  grid-template-columns: 8.5rem minmax(0, 1fr);
  grid-template-areas:
    "portrait facts"
    "badges   badges"
    "note     note";
  gap: var(--space-4);
  padding: var(--space-5);
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
}

.close {
  position: absolute;
  top: var(--space-3);
  right: var(--space-3);
  z-index: 1;
  width: 1.75rem;
  height: 1.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-sunken);
  border: 1px solid var(--border);
  border-radius: var(--radius-full);
  font-size: var(--text-md);
  line-height: 1;
  color: var(--text-muted);
  cursor: pointer;
}

.close:hover {
  color: var(--text);
  border-color: var(--border-strong);
}

/* ------------------------------------------------------- left column */
.portrait {
  grid-area: portrait;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
}

/*
  The camera slot, at the size a selected tile will stream at. Square
  rather than the grid's 4:3: a portrait of one person reads better square,
  and this is the only place a higher-resolution feed is requested.
*/
.slot {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  aspect-ratio: 1;
  background: var(--bg-sunken);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
}

.avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 4rem;
  height: 4rem;
  border-radius: var(--radius-full);
  background: var(--indigo-soft);
  color: var(--indigo);
  font-size: var(--text-xl);
  font-weight: 600;
}

.mentorTag {
  background: var(--indigo-soft);
  color: var(--indigo);
  border-radius: var(--radius-full);
  padding: 0.125rem 0.5rem;
  font-size: var(--text-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  white-space: nowrap;
}

/* ------------------------------------------------------ right column */
.facts {
  grid-area: facts;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  margin: 0;
  /* Clears the close button. */
  padding-right: var(--space-5);
}

.name {
  margin: 0;
  font-size: var(--text-xl);
  font-weight: 600;
  color: var(--text);
  line-height: var(--leading-tight);
}

/*
  Figures in a 2x2 block rather than a row: four across in a half-width
  column would be four cramped columns, and these are the numbers the card
  exists to show.
*/
.figures {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-3);
}

.fact {
  display: flex;
  flex-direction: column;
  gap: 0.0625rem;
}

.factLabel {
  font-size: var(--text-xs);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-subtle);
}

.factValue {
  font-size: var(--text-lg);
  font-weight: 600;
  color: var(--text);
  font-variant-numeric: tabular-nums;
  line-height: var(--leading-tight);
}

.unit {
  font-size: var(--text-xs);
  font-weight: 400;
  color: var(--text-muted);
}

/* ------------------------------------------------------------ badges */
/*
  Every badge is rendered, earned or not. The faded ones are what give a
  viewer something to aim at; a cabinet of only what you have is empty on
  day one and says nothing about what to do next.
*/
.badges {
  grid-area: badges;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding-top: var(--space-3);
  border-top: 1px solid var(--border);
}

.badgeTitle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0;
  font-size: var(--text-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-subtle);
}

.badgeCount {
  font-weight: 500;
  letter-spacing: 0;
  text-transform: none;
  color: var(--text-muted);
}

/*
  *** A FIXED FOUR COLUMNS, NOT auto-fit ***
  auto-fit packed six badges onto the first row and orphaned the seventh,
  which reads as a layout bug rather than a set of seven. Four columns
  gives a deliberate 4 + 3, and the second row is centred so the gap sits
  either side instead of trailing off to the right.

  It also survives the set changing: an eighth badge makes a clean 4 + 4,
  and a sixth would make 4 + 2 — still centred, still deliberate.
*/
.badgeList {
  list-style: none;
  margin: 0;
  padding: 0;
  /*
    Flex, not grid. A short last row in a grid sits left-aligned under the
    full row above it; flex-wrap plus `justify-content: center` centres it,
    which is what makes 4 + 3 look chosen instead of broken.

    The basis is a quarter of the row minus its share of the gaps, so four
    fit exactly and the fifth wraps.
  */
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--space-2);
}

.badgeList > li {
  flex: 0 0 calc((100% - 3 * var(--space-2)) / 4);
}

.badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  /* Labels differ in length — "Marathon" against "Week strong" — and a
     one-line tile beside a two-line one makes the row look broken. The
     icon sits at the top of every tile and the label fills what is left. */
  justify-content: flex-start;
  min-height: 4.25rem;
  gap: 0.25rem;
  padding: var(--space-2) 0.25rem;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  /*
    Unearned: visible but clearly not held. Dimmed rather than hidden, and
    the `title` carries the rule, so a faded icon is a goal and not a
    puzzle.
  */
  opacity: 0.45;
  cursor: help;
  transition: opacity 120ms ease;
}

.badge:hover {
  opacity: 0.75;
}

.badgeEarned,
.badgeEarned:hover {
  opacity: 1;
  background: var(--indigo-soft);
  border-color: var(--indigo);
}

.badgeIcon {
  font-size: var(--text-lg);
  line-height: 1;
  color: var(--text-muted);
}

.badgeEarned .badgeIcon {
  color: var(--indigo);
}

.badgeLabel {
  font-size: var(--text-xs);
  text-align: center;
  color: var(--text-muted);
  line-height: var(--leading-tight);
}

.badgeEarned .badgeLabel {
  color: var(--text);
  font-weight: 600;
}

/* -------------------------------------------------------------- note */
.note {
  grid-area: note;
  margin: 0;
  padding-top: var(--space-3);
  border-top: 1px solid var(--border);
  font-size: var(--text-sm);
  color: var(--text-muted);
  text-align: center;
}

/* ------------------------------------------------------------- phone */
/*
  Below the two-column minimum the portrait goes above the figures. The
  portrait shrinks rather than filling the width — a full-width avatar box
  on a phone is most of the screen before any information appears.
*/
@media (max-width: 30rem) {
  .card {
    grid-template-columns: 1fr;
    grid-template-areas:
      "portrait"
      "facts"
      "badges"
      "note";
    padding: var(--space-4);
  }

  .portrait {
    flex-direction: row;
    align-items: center;
    gap: var(--space-3);
  }

  .slot {
    width: 4.5rem;
    flex-shrink: 0;
  }

  .avatar {
    width: 3rem;
    height: 3rem;
    font-size: var(--text-md);
  }

  .facts {
    padding-right: 0;
  }

  /* Three across on a phone: four would make each tile narrower than the
     word "Marathon". Seven then reads 3 + 3 + 1, so the last row is
     centred by the same flex rule. */
  .badgeList > li {
    flex: 0 0 calc((100% - 2 * var(--space-2)) / 3);
  }
}

/* --------------------------------------------------- mentor variant */
/* Their title under the name: what they teach is the reason to tap. */
.role {
  margin: 0;
  font-size: var(--text-sm);
  color: var(--text-muted);
  line-height: var(--leading-tight);
}

.tags {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.tag {
  background: var(--indigo-soft);
  color: var(--indigo);
  border: 1px solid var(--indigo);
  border-radius: var(--radius-full);
  padding: 0.125rem 0.625rem;
  font-size: var(--text-xs);
  font-weight: 600;
}

.bio {
  margin: 0;
  font-size: var(--text-sm);
  color: var(--text-muted);
  line-height: var(--leading-normal);
  /* A long bio must not make the dialog taller than the screen. */
  max-height: 6rem;
  overflow-y: auto;
}
